import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  CheckCircle2, AlertTriangle, XCircle, MinusCircle, Upload, Link2, Eye,
  Pencil, ChevronDown, ChevronUp, Shield, Send, Clock, FileText, Trash2,
  Plus, History, RotateCcw, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  complete: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700', label: 'Complete' },
  in_progress: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700', label: 'In Progress' },
  missing: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', label: 'Missing' },
  not_applicable: { icon: MinusCircle, color: 'text-slate-400', bg: 'bg-slate-50 border-slate-200', badge: 'bg-slate-100 text-slate-500', label: 'N/A' },
};

const RECORD_TYPES = [
  { value: 'supporting_attachment', label: 'Supporting Attachment' },
  { value: 'annexure', label: 'Annexure' },
  { value: 'notice', label: 'Notice' },
  { value: 'programme_extract', label: 'Programme Extract' },
  { value: 'delay_analysis', label: 'Delay Analysis' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'drawing', label: 'Drawing' },
  { value: 'instruction', label: 'Instruction' },
  { value: 'cost_backup', label: 'Cost Backup' },
  { value: 'photo_evidence', label: 'Photo Evidence' },
  { value: 'other', label: 'Other' },
];

const DELAY_METHODS = [
  { value: 'time_impact_analysis', label: 'Time Impact Analysis (TIA)' },
  { value: 'impacted_as_planned', label: 'Impacted As-Planned' },
  { value: 'collapsed_as_built', label: 'Collapsed As-Built' },
  { value: 'as_planned_vs_as_built', label: 'As-Planned vs As-Built' },
  { value: 'windows_analysis', label: 'Windows Analysis' },
  { value: 'other', label: 'Other' },
];

// ──────────── ReadinessItemCard ────────────
function ReadinessItemCard({ item, onAction, onOverride, onRemoveOverride }) {
  const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.missing;
  const Icon = cfg.icon;

  return (
    <div className={`border rounded-lg p-3 sm:p-4 transition-all ${cfg.bg}`} data-testid={`readiness-item-${item.code}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
          <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${cfg.color}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-xs sm:text-sm text-slate-900">{item.category}. {item.name}</span>
              <Badge className={`text-[10px] ${cfg.badge}`}>{cfg.label}</Badge>
            </div>
            <p className="text-xs text-slate-600 mt-1">{item.message}</p>
            {item.evidence_summary && item.status === 'complete' && (
              <p className="text-xs text-green-700 mt-1 font-medium">{item.evidence_summary}</p>
            )}
            {item.missing_reasons?.length > 0 && item.status !== 'complete' && item.status !== 'not_applicable' && (
              <ul className="mt-2 space-y-1">
                {item.missing_reasons.map((r, i) => (
                  <li key={i} className="text-xs text-red-600 flex items-start gap-1.5">
                    <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-7 sm:ml-0">
          {item.status !== 'not_applicable' && (
            <Button
              size="sm"
              variant={item.status === 'complete' ? 'outline' : 'default'}
              className={`text-xs h-7 ${item.status === 'complete' ? 'border-green-300 text-green-700' : 'bg-slate-900 text-white'}`}
              onClick={() => onAction(item)}
              data-testid={`readiness-action-${item.code}`}
            >
              {item.action_type === 'view' && <Eye className="h-3 w-3 mr-1" />}
              {item.action_type === 'edit' && <Pencil className="h-3 w-3 mr-1" />}
              {item.action_type === 'upload' && <Upload className="h-3 w-3 mr-1" />}
              {item.action_type === 'link' && <Link2 className="h-3 w-3 mr-1" />}
              {item.action_label}
            </Button>
          )}
          {item.status === 'not_applicable' && item.code !== 'cost_claim' && (
            <Button size="sm" variant="ghost" className="text-xs h-7 text-slate-400" onClick={() => onRemoveOverride(item.code)}>
              <RotateCcw className="h-3 w-3 mr-1" /> Restore
            </Button>
          )}
          {item.status !== 'not_applicable' && item.status !== 'complete' && (
            <Button size="sm" variant="ghost" className="text-xs h-7 text-slate-400" onClick={() => onOverride(item)} title="Mark as Not Applicable">
              <MinusCircle className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────── Main Panel ────────────
export default function ClaimReadinessPanel({ claimId, claim, onClaimUpdate, onNavigateToEvent }) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [showParticularsDialog, setShowParticularsDialog] = useState(null); // item code
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Particulars form state
  const [particularsForm, setParticularsForm] = useState({
    contractual_clause: claim?.contractual_clause || '',
    entitlement_basis: claim?.entitlement_basis || '',
    effect_on_works: claim?.effect_on_works || '',
    delay_analysis_method: claim?.delay_analysis_method || '',
    claimed_days_derivation: claim?.claimed_days_derivation || '',
    revised_completion_date: claim?.revised_completion_date || '',
    sectional_eot_impact: claim?.sectional_eot_impact || '',
    cost_buildup_notes: claim?.cost_buildup_notes || '',
  });

  // Supporting record form
  const [recordForm, setRecordForm] = useState({
    record_type: 'supporting_attachment',
    file_name: '',
    file_url: '',
    linked_record_ref: '',
    annexure_label: '',
    annexure_title: '',
    annexure_description: '',
  });

  // Override form
  const [overrideReason, setOverrideReason] = useState('');

  const loadReadiness = useCallback(async () => {
    try {
      const res = await api.claims.getReadiness(claimId);
      setReadiness(res.data);
    } catch (err) {
      console.error('Failed to load readiness', err);
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => { loadReadiness(); }, [loadReadiness]);

  useEffect(() => {
    setParticularsForm({
      contractual_clause: claim?.contractual_clause || '',
      entitlement_basis: claim?.entitlement_basis || '',
      effect_on_works: claim?.effect_on_works || '',
      delay_analysis_method: claim?.delay_analysis_method || '',
      claimed_days_derivation: claim?.claimed_days_derivation || '',
      revised_completion_date: claim?.revised_completion_date || '',
      sectional_eot_impact: claim?.sectional_eot_impact || '',
      cost_buildup_notes: claim?.cost_buildup_notes || '',
    });
  }, [claim]);

  const handleAction = (item) => {
    // For edit actions open the particulars dialog for the relevant section
    if (item.action_type === 'edit') {
      setShowParticularsDialog(item.code);
    } else if (item.action_type === 'upload' || item.action_type === 'link') {
      setRecordForm(prev => ({ ...prev, record_type: getRecordTypeForItem(item.code) }));
      setShowRecordDialog(true);
    } else if (item.action_type === 'view') {
      if (item.code === 'event_details' && onNavigateToEvent) {
        onNavigateToEvent();
      } else {
        setShowParticularsDialog(item.code);
      }
    }
  };

  const getRecordTypeForItem = (code) => {
    const map = {
      delay_analysis: 'delay_analysis',
      supporting_attachments: 'supporting_attachment',
      annexures: 'annexure',
      notice_compliance: 'notice',
      cost_claim: 'cost_backup',
    };
    return map[code] || 'supporting_attachment';
  };

  const handleSaveParticulars = async () => {
    try {
      await api.claims.updateParticulars(claimId, particularsForm);
      toast.success('Particulars updated');
      setShowParticularsDialog(null);
      if (onClaimUpdate) onClaimUpdate();
      loadReadiness();
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const handleAddRecord = async () => {
    if (!recordForm.file_name && !recordForm.linked_record_ref) {
      toast.error('Please provide a file name or record reference');
      return;
    }
    try {
      await api.claims.addSupportingRecord(claimId, recordForm);
      toast.success('Record added');
      setShowRecordDialog(false);
      setRecordForm({ record_type: 'supporting_attachment', file_name: '', file_url: '', linked_record_ref: '', annexure_label: '', annexure_title: '', annexure_description: '' });
      loadReadiness();
    } catch (err) {
      toast.error('Failed to add record');
    }
  };

  const handleOverride = async () => {
    if (!overrideReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    try {
      await api.claims.addOverride(claimId, {
        item_code: showOverrideDialog.code,
        override_type: 'not_applicable',
        override_reason: overrideReason,
      });
      toast.success(`${showOverrideDialog.name} marked as Not Applicable`);
      setShowOverrideDialog(null);
      setOverrideReason('');
      loadReadiness();
    } catch (err) {
      toast.error('Failed to apply override');
    }
  };

  const handleRemoveOverride = async (itemCode) => {
    try {
      await api.claims.removeOverride(claimId, itemCode);
      toast.success('Override removed');
      loadReadiness();
    } catch (err) {
      toast.error('Failed to remove override');
    }
  };

  const handleGatedSubmit = async () => {
    setSubmitting(true);
    try {
      await api.claims.gatedSubmit(claimId);
      toast.success('Claim submitted to PM successfully');
      if (onClaimUpdate) onClaimUpdate();
      loadReadiness();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        toast.error(`Submission blocked: ${detail.missing_items?.length || 0} items outstanding`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Submission failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const loadAuditLog = async () => {
    try {
      const res = await api.claims.getAuditLog(claimId);
      setAuditLog(res.data);
      setShowAudit(true);
    } catch {
      toast.error('Failed to load audit log');
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-slate-200 rounded w-1/3" />
            <div className="h-3 bg-slate-200 rounded w-1/2" />
            <div className="h-20 bg-slate-200 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!readiness) return null;

  const completedPct = readiness.total_required > 0 ? Math.round((readiness.completed / readiness.total_required) * 100) : 0;
  const isReady = readiness.overall_ready;
  const isSubmitted = claim?.status === 'submitted';

  return (
    <>
      <Card className={`border-2 ${isReady ? 'border-green-300' : 'border-amber-300'} shadow-sm`} data-testid="claim-readiness-panel">
        <CardContent className="p-0">
          {/* Header */}
          <button
            onClick={() => setExpanded(!expanded)}
            className={`w-full flex items-center justify-between p-4 sm:p-5 ${isReady ? 'bg-green-50' : 'bg-amber-50'} rounded-t-lg`}
            data-testid="readiness-toggle"
          >
            <div className="flex items-center gap-3">
              <Shield className={`h-5 w-5 ${isReady ? 'text-green-600' : 'text-amber-600'}`} />
              <div className="text-left">
                <h3 className="font-bold text-sm sm:text-base text-slate-900">Claim Submission Readiness</h3>
                <p className={`text-xs ${isReady ? 'text-green-700' : 'text-amber-700'}`}>
                  {readiness.completed} of {readiness.total_required} required items complete &middot; {readiness.status_label}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-lg font-black ${isReady ? 'text-green-600' : 'text-amber-600'}`}>{completedPct}%</span>
              {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </div>
          </button>

          {/* Progress bar */}
          <div className="h-1.5 bg-slate-200">
            <div
              className={`h-full transition-all duration-500 ${isReady ? 'bg-green-500' : completedPct > 50 ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${completedPct}%` }}
            />
          </div>

          {expanded && (
            <div className="p-4 sm:p-5 space-y-5">
              {/* Missing items summary */}
              {!isReady && readiness.missing_summary.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="missing-summary">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-800">
                        Submission blocked: {readiness.missing_summary.length} required item{readiness.missing_summary.length > 1 ? 's' : ''} outstanding
                      </p>
                      <ul className="mt-2 space-y-1">
                        {readiness.missing_summary.map((msg, i) => (
                          <li key={i} className="text-xs text-red-700 flex items-start gap-1.5">
                            <XCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /> {msg}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {isReady && !isSubmitted && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="ready-summary">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <p className="text-sm font-semibold text-green-800">
                      This claim is ready for submission. All required particulars, attachments and annexures have been completed.
                    </p>
                  </div>
                </div>
              )}

              {/* Readiness items */}
              <div className="space-y-2">
                {readiness.items.map((item) => (
                  <ReadinessItemCard
                    key={item.code}
                    item={item}
                    onAction={handleAction}
                    onOverride={(item) => setShowOverrideDialog(item)}
                    onRemoveOverride={handleRemoveOverride}
                  />
                ))}
              </div>

              {/* Actions row */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-3 border-t border-slate-200">
                <Button
                  onClick={handleGatedSubmit}
                  disabled={!isReady || isSubmitted || submitting}
                  className={`flex-1 h-11 font-bold text-sm ${isReady && !isSubmitted ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                  data-testid="gated-submit-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isSubmitted ? 'Claim Already Submitted' : isReady ? 'Submit Claim to PM' : `${readiness.total_required - readiness.completed} Items Remaining`}
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowRecordDialog(true)} className="text-xs" data-testid="add-record-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Record
                  </Button>
                  <Button variant="outline" size="sm" onClick={loadAuditLog} className="text-xs" data-testid="audit-log-btn">
                    <History className="h-3.5 w-3.5 mr-1" /> Audit Log
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ──── Particulars Edit Dialog ──── */}
      <Dialog open={!!showParticularsDialog} onOpenChange={() => setShowParticularsDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Claim Particulars</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {(showParticularsDialog === 'contractual_basis' || showParticularsDialog === 'cover_details') && (
              <>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase">Contractual Clause</label>
                  <Input
                    value={particularsForm.contractual_clause}
                    onChange={e => setParticularsForm(p => ({ ...p, contractual_clause: e.target.value }))}
                    placeholder="e.g. Clause 29.1 JBCC / Clause 20.1 FIDIC"
                    data-testid="input-contractual-clause"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase">Entitlement Basis</label>
                  <Textarea
                    value={particularsForm.entitlement_basis}
                    onChange={e => setParticularsForm(p => ({ ...p, entitlement_basis: e.target.value }))}
                    placeholder="Describe the basis for the claim entitlement..."
                    rows={4}
                    data-testid="input-entitlement-basis"
                  />
                </div>
              </>
            )}
            {showParticularsDialog === 'effect_on_works' && (
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Effect on the Works</label>
                <Textarea
                  value={particularsForm.effect_on_works}
                  onChange={e => setParticularsForm(p => ({ ...p, effect_on_works: e.target.value }))}
                  placeholder="Describe the impact on programme, activities, milestones..."
                  rows={6}
                  data-testid="input-effect-on-works"
                />
              </div>
            )}
            {showParticularsDialog === 'delay_analysis' && (
              <>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase">Delay Analysis Method</label>
                  <Select value={particularsForm.delay_analysis_method} onValueChange={v => setParticularsForm(p => ({ ...p, delay_analysis_method: v }))}>
                    <SelectTrigger data-testid="input-delay-method"><SelectValue placeholder="Select method..." /></SelectTrigger>
                    <SelectContent>
                      {DELAY_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase">Claimed Days Derivation</label>
                  <Textarea
                    value={particularsForm.claimed_days_derivation}
                    onChange={e => setParticularsForm(p => ({ ...p, claimed_days_derivation: e.target.value }))}
                    placeholder="Explain how claimed days were calculated..."
                    rows={3}
                    data-testid="input-days-derivation"
                  />
                </div>
              </>
            )}
            {showParticularsDialog === 'eot_claimed' && (
              <>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase">Revised Completion Date</label>
                  <Input
                    type="date"
                    value={particularsForm.revised_completion_date}
                    onChange={e => setParticularsForm(p => ({ ...p, revised_completion_date: e.target.value }))}
                    data-testid="input-revised-date"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase">Sectional EOT Impact</label>
                  <Textarea
                    value={particularsForm.sectional_eot_impact}
                    onChange={e => setParticularsForm(p => ({ ...p, sectional_eot_impact: e.target.value }))}
                    placeholder="Describe impact on sectional completion if applicable..."
                    rows={3}
                    data-testid="input-sectional-eot"
                  />
                </div>
              </>
            )}
            {showParticularsDialog === 'cost_claim' && (
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Cost Build-up Notes</label>
                <Textarea
                  value={particularsForm.cost_buildup_notes}
                  onChange={e => setParticularsForm(p => ({ ...p, cost_buildup_notes: e.target.value }))}
                  placeholder="Summarise cost build-up approach..."
                  rows={4}
                  data-testid="input-cost-notes"
                />
              </div>
            )}
            {(showParticularsDialog === 'particulars' || showParticularsDialog === 'internal_signoff' || showParticularsDialog === 'notice_compliance' || showParticularsDialog === 'event_details' || showParticularsDialog === 'supporting_attachments' || showParticularsDialog === 'annexures') && (
              <p className="text-sm text-slate-500">
                {showParticularsDialog === 'particulars' && 'The narrative content is managed in the main claim editor above. Ensure the claim content is comprehensive (minimum 50 characters).'}
                {showParticularsDialog === 'internal_signoff' && 'Internal sign-off can be completed from the claim actions menu.'}
                {showParticularsDialog === 'notice_compliance' && 'Notice compliance is evaluated automatically based on the linked notice and event dates.'}
                {showParticularsDialog === 'event_details' && 'Event details are managed on the Delay Event page. Navigate there to update.'}
                {showParticularsDialog === 'supporting_attachments' && 'Use the "Add Record" button to upload or link supporting documents.'}
                {showParticularsDialog === 'annexures' && 'Use the "Add Record" button with type "Annexure" to manage annexures.'}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowParticularsDialog(null)}>Cancel</Button>
            <Button onClick={handleSaveParticulars} data-testid="save-particulars-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Add Record Dialog ──── */}
      <Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Supporting Record</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase">Classification</label>
              <Select value={recordForm.record_type} onValueChange={v => setRecordForm(f => ({ ...f, record_type: v }))}>
                <SelectTrigger data-testid="record-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RECORD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase">File Name / Document Title</label>
              <Input value={recordForm.file_name} onChange={e => setRecordForm(f => ({ ...f, file_name: e.target.value }))} placeholder="e.g. Delay Analysis Report v2.pdf" data-testid="record-filename" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase">File URL or Link</label>
              <Input value={recordForm.file_url} onChange={e => setRecordForm(f => ({ ...f, file_url: e.target.value }))} placeholder="https://... or leave blank for manual reference" data-testid="record-url" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase">Linked Record Reference</label>
              <Input value={recordForm.linked_record_ref} onChange={e => setRecordForm(f => ({ ...f, linked_record_ref: e.target.value }))} placeholder="e.g. RFI-003, SI-012, N-004" data-testid="record-ref" />
            </div>
            {recordForm.record_type === 'annexure' && (
              <>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-700 uppercase">Label</label>
                    <Input value={recordForm.annexure_label} onChange={e => setRecordForm(f => ({ ...f, annexure_label: e.target.value }))} placeholder="A" data-testid="annexure-label" />
                  </div>
                  <div className="col-span-3">
                    <label className="text-xs font-bold text-slate-700 uppercase">Annexure Title</label>
                    <Input value={recordForm.annexure_title} onChange={e => setRecordForm(f => ({ ...f, annexure_title: e.target.value }))} placeholder="Correspondence Record" data-testid="annexure-title" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase">Description</label>
                  <Textarea value={recordForm.annexure_description} onChange={e => setRecordForm(f => ({ ...f, annexure_description: e.target.value }))} placeholder="Brief description of this annexure..." rows={2} data-testid="annexure-desc" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordDialog(false)}>Cancel</Button>
            <Button onClick={handleAddRecord} data-testid="save-record-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Override Dialog ──── */}
      <Dialog open={!!showOverrideDialog} onOpenChange={() => setShowOverrideDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as Not Applicable</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            You are marking <strong>{showOverrideDialog?.name}</strong> as not applicable for this claim. This action will be recorded in the audit trail.
          </p>
          <Textarea
            value={overrideReason}
            onChange={e => setOverrideReason(e.target.value)}
            placeholder="Provide a reason for this override..."
            rows={3}
            data-testid="override-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowOverrideDialog(null); setOverrideReason(''); }}>Cancel</Button>
            <Button onClick={handleOverride} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-override-btn">
              Confirm Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Audit Log Dialog ──── */}
      <Dialog open={showAudit} onOpenChange={setShowAudit}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Claim Audit Trail</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {auditLog.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No audit entries yet</p>}
            {auditLog.map((entry) => (
              <div key={entry.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <History className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700">{entry.event_description}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {entry.user_name} &middot; {new Date(entry.timestamp).toLocaleString()}
                  </p>
                </div>
                <Badge className="bg-slate-100 text-slate-500 text-[10px] flex-shrink-0">{entry.event_type}</Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
